// Product Development - StatusSelection component (Clinical Supply)
// Only shows PO Status and Batch Status (not Project and Campaign)
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  withStyles,
  Box,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useIsMounted } from "../../../../../../hooks/useIsMounted";
import { appUrl } from "../../../../../../../../utils/config";

// IOSSwitch matching Commercial Supply styling
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
  // Custom styles for always-enabled statuses
  alwaysEnabled: {
    "& .MuiSwitch-track": {
      backgroundColor: "#8BC6FD !important",
    },
    "&$checked": {
      "& + $track": {
        backgroundColor: "#8BC6FD !important",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#8BC6FD !important",
      },
    },
  },
}))(({ classes, isAlwaysEnabled, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const StatusSelection = ({
  formData,
  updateStepData,
  classes: parentClasses,
  handleNext,
  handleBack,
  activeStep,
  stepsLength,
  markApiCallSuccessful,
  fetchFGStatusesAPI,
  hideStepActions = false,
  readOnly = false,
  isStatusEditMode = false,
  onValidationChange,
}) => {
  const [stepData, setStepData] = useState(() => {
    const saved = formData?.fgStatuses || formData?.statusSelection || {};
    return {
      poStatuses: saved.poStatuses || [],
      batchStatuses: saved.batchStatuses || [],
    };
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropdownAPIsLoadedRef = useRef(false);
  const prevFormDataRef = useRef(null); // Track previous formData to prevent unnecessary updates
  const [isInitialSaveSuccess, setIsInitialSaveSuccess] = useState(() => {
    const savedValue = sessionStorage.getItem('pdAddedStatusSelection');
    return savedValue === "true";
  });
  // Persist emptyFgStatuses in sessionStorage - matching Commercial Supply
  const [emptyFgStatuses, setEmptyFgStatusesState] = useState(() => {
    const saved = sessionStorage.getItem('pdEmptyFgStatuses');
    return saved === 'true';
  });
  // Helper to set and persist emptyFgStatuses
  const setEmptyFgStatuses = (val) => {
    setEmptyFgStatusesState(val);
    if (val) {
      sessionStorage.setItem('pdEmptyFgStatuses', 'true');
    } else {
      sessionStorage.removeItem('pdEmptyFgStatuses');
    }
  };
  
  // Get location for editPendingStatus check - matching Commercial Supply
  const location = useLocation();
  const editPendingStatus = location.state?.isPending && location.state?.isEdit;
  const isNotPending = location.state?.isEdit && location.state?.isNotPending;
  const selectedProduct = location.state?.selectedProduct || {};
  const selectedProductFormData = location.state?.selectedProductFormData;
  const viewOnlyProductDetails = location.state?.viewProductDetails;
  const selectedCollabrixID = location.state?.productDetails?.collabrixProductID;
  const [updateModeData, setUpdateModeData] = useState(null);
  
  // Read selectedReplicatedFromProductID from sessionStorage - matching Commercial Supply
  const [selectedReplicatedFromProductID, setSelectedReplicatedFromProductID] = useState(
    sessionStorage.getItem("selectedReplicatedFromProductID") || ""
  );
  useEffect(() => {
    setSelectedReplicatedFromProductID(sessionStorage.getItem("selectedReplicatedFromProductID") || "");
  }, []);

  // Effect for edit pending status / coming back from Review after initial save
  // Match Commercial behaviour: fetch product details by collabrixProductID,
  // then fetch FG statuses and overlay only the activated ones from the backend.
  // NOTE: the pending/edit flow that calls fetchProductDetails and then fetchFGStatus
  // is implemented in the Commercial StatusSelection; for Clinical we rely on
  // the shared fetchFGStatus + merge logic below, so we don't reference apiBaseUrl
  // before its declaration.

  // API base URL
  const apiBaseUrl = React.useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // isMounted hook - matching Commercial Supply
  const isMounted = useIsMounted();

  // Keep a ref of latest formData for comparison and payload building (matching Commercial Supply pattern)
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const fetchFGStatus = async () => {
    if (!isMounted()) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(
        `${apiBaseUrl}/fetchFGStatuses/true`
      );
      if (!isMounted()) return;
      if (response?.data?.data) {
        const apiData = response.data.data;
        const isAddFlow = !isStatusEditMode && !viewOnlyProductDetails;

        // Process the data based on whether we're in add flow or edit mode - matching Commercial Supply exactly
        const processedData = {
          poStatuses: Array.isArray(apiData.poStatuses)
            ? apiData.poStatuses.map((status) => {
              return {
                status_id: status.status_id,
                status_name: status.status_name,
                value: isAddFlow ? status.activated : false
              };
            })
            : [],
          // Clinical Supply only: Batch statuses
          batchStatuses: Array.isArray(apiData.batchStatuses)
            ? apiData.batchStatuses.map((status) => {
              return {
                status_id: status.status_id,
                status_name: status.status_name,
                value: isAddFlow ? status.activated : false
              };
            })
            : [],
        };

        // Merge API data with saved fgStatuses (from formData or updateModeData):
        // this is what keeps the user's ON/OFF selections when we come back
        // from Review or resume after cancel – just like Commercial.
        if (!emptyFgStatuses) {
          const savedFg =
            (formData?.fgStatuses &&
              (Array.isArray(formData.fgStatuses.poStatuses) ||
               Array.isArray(formData.fgStatuses.batchStatuses)))
              ? formData.fgStatuses
              : updateModeData?.productFGStatuses?.fgStatusesJSON || {};

          if (savedFg) {
            // PO statuses: ON when value === true OR activated === true
            if (Array.isArray(savedFg.poStatuses)) {
              processedData.poStatuses = processedData.poStatuses.map(status => {
                const saved = savedFg.poStatuses.find(
                  s => s && s.status_id === status.status_id
                );
                const isOn = saved && (saved.value === true || saved.activated === true);
                return {
                  ...status,
                  value: isOn,
                  activated: isOn,
                };
              });
            }

            // Clinical only: Batch statuses – same rule as PO
            if (Array.isArray(savedFg.batchStatuses)) {
              processedData.batchStatuses = processedData.batchStatuses.map(status => {
                const saved = savedFg.batchStatuses.find(
                  s => s && s.status_id === status.status_id
                );
                const isOn = saved && (saved.value === true || saved.activated === true);
                return {
                  ...status,
                  value: isOn,
                  activated: isOn,
                };
              });
            }
          }
        }

        setStepData(processedData);
        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", processedData);
        }
      } else {
        setError("Invalid data structure received from API");
      }
    } catch (error) {
      if (isMounted()) {
        console.error(error, "error-saveFGStatuses")
        setError("An error occurred while saving. Please try again later.");
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  // Pending/edit + "back from Review" flow – match Commercial:
  // 1) fetchProductDetails(collabrixProductID)
  // 2) fetchFGStatuses
  // 3) overlay activated PO/Batch from backend onto FG master list
  useEffect(() => {
    // Match Commercial: drive this effect off formData.productDetails.collabrixProductID
    // and flags, not off callback identities from props.
    const collabrixProductID = formData?.productDetails?.collabrixProductID;
    if ((editPendingStatus || isInitialSaveSuccess) && collabrixProductID && apiBaseUrl) {
      const callData = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductDetails/${collabrixProductID}`
          );
          if (response.data && response.data.status) {
            const apiData = response.data.data;
            setUpdateModeData(apiData);
            const activatedStatuses = apiData.productFGStatuses?.fgStatusesJSON || {};

            if (Object.keys(activatedStatuses)?.length === 0) {
              setEmptyFgStatuses(true);
              await fetchFGStatus();
              return;
            }

            // First load full FG master list, then overlay activated ones from backend
            await fetchFGStatus();

            setStepData(prevData => {
              const updatedData = { ...prevData };

              // PO statuses from backend
              if (Array.isArray(activatedStatuses.poStatuses)) {
                updatedData.poStatuses = prevData.poStatuses.map(status => {
                  const isActivated = activatedStatuses.poStatuses.some(
                    s => s.status_id === status.status_id && s.activated === true
                  );
                  return {
                    ...status,
                    value: isActivated,
                    activated: isActivated,
                  };
                });
              }

              // Clinical only: Batch statuses from backend
              if (Array.isArray(activatedStatuses.batchStatuses)) {
                updatedData.batchStatuses = prevData.batchStatuses.map(status => {
                  const isActivated = activatedStatuses.batchStatuses.some(
                    s => s.status_id === status.status_id && s.activated === true
                  );
                  return {
                    ...status,
                    value: isActivated,
                    activated: isActivated,
                  };
                });
              }

              if (typeof updateStepData === "function") {
                updateStepData("fgStatuses", updatedData);
              }
              return updatedData;
            });
          } else {
            setError("Failed to fetch product details.");
          }
        } catch (error) {
          setError("An error occurred while fetching product details.");
        } finally {
          if (isMounted()) {
            setIsLoading(false);
          }
        }
      };

      if (!readOnly) {
        callData();
      }
    }
    // Dependencies intentionally mirror Commercial: do NOT include updateStepData or isMounted,
    // they are stable utilities and including them would cause unnecessary re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPendingStatus, isInitialSaveSuccess, formData?.productDetails?.collabrixProductID]);

  // Initialize data from formData if available - matching Commercial Supply exactly (line 312-372)
  useEffect(() => {
    if (isMounted() && formData?.fgStatuses) {
      const currentFormData = JSON.stringify(formData.fgStatuses);
      if (currentFormData !== JSON.stringify(prevFormDataRef.current)) {
        // Replica flow: show only activated statuses - matching Commercial Supply
        if (
          selectedReplicatedFromProductID &&
          location.state?.addNewProductConfig &&
          readOnly
        ) {
          const viewModeData = {
            poStatuses: (formData.fgStatuses.poStatuses || [])
              .filter(status => status.value === true || status.activated === true)
              .map(status => ({ ...status, value: true, activated: true })),
            // Clinical Supply only: Batch statuses
            batchStatuses: (formData.fgStatuses.batchStatuses || [])
              .filter(status => status.value === true || status.activated === true)
              .map(status => ({ ...status, value: true, activated: true })),
          };
          setStepData(viewModeData);
          prevFormDataRef.current = viewModeData;
          setIsLoading(false);
        }
        // General readOnly/viewOnly: show all statuses, toggled ON for selected/activated - matching Commercial Supply
        else if (viewOnlyProductDetails || readOnly) {
          const allStatusesWithActivated = {
            poStatuses: (formData.fgStatuses.poStatuses || []).map(status => {
              return {
                ...status,
                value: status.value === true || status.activated === true,
                activated: status.value === true || status.activated === true,
              };
            }),
            // Clinical Supply only: Batch statuses
            batchStatuses: (formData.fgStatuses.batchStatuses || []).map(status => {
              return {
                ...status,
                value: status.value === true || status.activated === true,
                activated: status.value === true || status.activated === true,
              };
            }),
          };
          setStepData(allStatusesWithActivated);
          prevFormDataRef.current = allStatusesWithActivated;
          setIsLoading(false);
        }
        // Other cases - matching Commercial Supply (non-pending, wizard flow):
        else if (isNotPending && !editPendingStatus && !isStatusEditMode) {
          const viewModeData = {
            poStatuses: (formData.fgStatuses.poStatuses || []).filter(
              status => status && (status.value === true || status.activated === true)
            ),
            // Clinical Supply only: Batch statuses
            batchStatuses: (formData.fgStatuses.batchStatuses || []).filter(
              status => status && (status.value === true || status.activated === true)
            ),
          };
          setStepData(viewModeData);
          prevFormDataRef.current = viewModeData;
          setIsLoading(false);
        } else {
          // Matching Commercial Supply line 352 - use formData directly
          setStepData(formData.fgStatuses);
          prevFormDataRef.current = formData.fgStatuses;
          setIsLoading(false);
        }
      } else {
        setIsLoading(false); // fallback if data is unchanged
      }
    } else {
      setIsLoading(false); // fallback if no fgStatuses
    }
  }, [
    formData?.fgStatuses,
    isMounted(),
    isNotPending,
    editPendingStatus,
    isStatusEditMode,
    viewOnlyProductDetails,
    readOnly,
    selectedReplicatedFromProductID,
    location.state?.addNewProductConfig
  ]);

  // Effect for status edit mode - matching Commercial Supply exactly (line 264-310)
  useEffect(() => {
    if (isStatusEditMode && !readOnly) {
      const updateStatusData = async () => {
        try {
          setIsLoading(true);
          // First fetch all available statuses
          await fetchFGStatus();

          // Then update with the activated ones from formData - matching Commercial Supply exactly
          const activatedStatuses = stepData || {};
          
          setStepData(prevData => {
            const updatedData = { ...prevData };

            // Update PO Statuses - matching Commercial Supply exactly
            if (Array.isArray(activatedStatuses.poStatuses)) {
              updatedData.poStatuses = prevData.poStatuses.map(status => {
                const isActivated = activatedStatuses.poStatuses.some(
                  activated => activated.status_id === status.status_id && activated.value === true
                );
                return {
                  ...status,
                  value: isActivated
                };
              });
            }

            // Clinical Supply only: Update Batch Statuses
            if (Array.isArray(activatedStatuses.batchStatuses)) {
              updatedData.batchStatuses = prevData.batchStatuses.map(status => {
                const isActivated = activatedStatuses.batchStatuses.some(
                  activated => activated.status_id === status.status_id && activated.value === true
                );
                return {
                  ...status,
                  value: isActivated
                };
              });
            }

            // Update parent component - matching Commercial Supply
            if (typeof updateStepData === "function") {
              updateStepData("fgStatuses", updatedData);
            }
            return updatedData;
          });
        } catch (error) {
          setError("An error occurred while updating status data.");
        } finally {
          if (isMounted()) {
            setIsLoading(false);
          }
        }
      };

      updateStatusData();
    }
  }, [isStatusEditMode]);

  // Match Commercial: only call fetchFGStatus for pure "add new" flow
  // (no replicated product, not readOnly, before first successful save).
  useEffect(() => {
    let callFetch = false;

    if (selectedReplicatedFromProductID) {
      callFetch = false;
    } else if (location.state?.addNewProductConfig && !isInitialSaveSuccess && !readOnly && !isStatusEditMode) {
      callFetch = true;
    } else {
      callFetch = false;
    }

    if (callFetch) {
      fetchFGStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReplicatedFromProductID, isInitialSaveSuccess, readOnly, isStatusEditMode, location.state?.addNewProductConfig]);

  // Fallback: after 1s, if still loading and no data, set loading to false - matching Commercial Supply (line 390-398)
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        if (isLoading) setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Set initial loading state - matching Commercial Supply (line 400-405)
  useEffect(() => {
    if (formData?.fgStatuses) {
      setIsLoading(false);
    }
  }, [formData?.fgStatuses]);

  // Handle toggle changes
  const handleToggleStatus = useCallback(
    (category, statusId, checked) => {
      setStepData((prevData) => {
        const updatedData = JSON.parse(JSON.stringify(prevData));
        if (category === "postatus" && updatedData.poStatuses) {
          const statusIndex = updatedData.poStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            updatedData.poStatuses[statusIndex].value = checked;
            updatedData.poStatuses[statusIndex].activated = checked;
          }
        } else if (category === "batchstatus" && updatedData.batchStatuses) {
          const statusIndex = updatedData.batchStatuses.findIndex(
            (item) => item && item.status_id === statusId
          );
          if (statusIndex !== -1) {
            updatedData.batchStatuses[statusIndex].value = checked;
            updatedData.batchStatuses[statusIndex].activated = checked;
          }
        }

        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", updatedData);
        }

        // Validate - at least one PO and at least one Batch status should be selected
        const hasPoSelected =
          Array.isArray(updatedData.poStatuses) &&
          updatedData.poStatuses.some(s => s && (s.value || s.activated));
        const hasBatchSelected =
          Array.isArray(updatedData.batchStatuses) &&
          updatedData.batchStatuses.some(s => s && (s.value || s.activated));
        const hasSelectedStatus = hasPoSelected && hasBatchSelected;
        if (typeof onValidationChange === "function") {
          onValidationChange(hasSelectedStatus);
        }

        return updatedData;
      });
    },
    [updateStepData, onValidationChange]
  );

  // Render status table - in Review (readOnly) we only show selected/activated
  // statuses, matching Commercial behaviour (API returns only what is active).
  const renderStatusTable = (title, statuses, category) => {
    const rows = Array.isArray(statuses)
      ? (readOnly
        ? statuses.filter(
            (s) => s && (s.value === true || s.activated === true)
          )
        : statuses)
      : [];

    if (!rows || rows.length === 0) {
      return (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>{title}</TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="right">
                  Selection
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow className="no-status-row">
                <TableCell colSpan={2} align="center">
                  No statuses are available
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>{title}</TableCell>
              <TableCell style={{ fontWeight: "bold" }} align="right">
                Selection
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {rows.length > 0 ? (
            rows.map((status) => {
                const isChecked = status?.value || false;
                const isDisabled = readOnly;
                
                return (
                  <TableRow key={status?.status_id || Math.random()}>
                    <TableCell>{status?.status_name || "Unknown"}</TableCell>
                    <TableCell align="right">
                      <span style={{ position: "relative" }}>
                        <IOSSwitch
                          className="ios-switch"
                          checked={isChecked}
                          onChange={(e) =>
                            handleToggleStatus(
                              category,
                              status?.status_id,
                              e.target.checked
                            )
                          }
                          color="primary"
                          disabled={isDisabled}
                          isAlwaysEnabled={false}
                        />
                        {isChecked && <Check className="checked-switch" />}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="no-status-row">
                <TableCell colSpan={2} align="center">
                  No statuses are available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Save current selections and move to next step - matching Commercial Supply
  const handleSaveAndNext = useCallback(async () => {
    console.groupCollapsed("Clinical StatusSelection: handleSaveAndNext");
    if (!apiBaseUrl) {
      console.error("Clinical StatusSelection: API base URL not available", {
        apiBaseUrl,
      });
      console.groupEnd();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Format data to include ONLY selected statuses with activated property
      // Clinical Supply has both PO and Batch statuses
      const formattedData = {
        poStatuses: Array.isArray(stepData.poStatuses)
          ? stepData.poStatuses
              .filter((status) => status && (status.value === true || status.activated === true))
              .map(({ status_id, status_name }) => ({
                status_id,
                status_name,
                activated: true
              }))
          : [],
        batchStatuses: Array.isArray(stepData.batchStatuses)
          ? stepData.batchStatuses
              .filter((status) => status && (status.value === true || status.activated === true))
              .map(({ status_id, status_name }) => ({
                status_id,
                status_name,
                activated: true
              }))
          : [],
      };

      // Validate that at least one PO and at least one Batch status is selected
      console.log("Clinical StatusSelection: formattedData", formattedData);
      const hasPoSelected = formattedData.poStatuses.length > 0;
      const hasBatchSelected = formattedData.batchStatuses.length > 0;

      if (!hasPoSelected || !hasBatchSelected) {
        console.warn(
          "Clinical StatusSelection: validation failed - need at least one PO and one Batch status",
          { hasPoSelected, hasBatchSelected }
        );
        setError(
          "Please select at least one PO status and at least one Batch status before proceeding"
        );
        setIsSaving(false);
        console.groupEnd();
        return;
      }

      // Get current form data
      const currentFormData = formDataRef.current;
      const collabrixProductID = currentFormData?.productDetails?.collabrixProductID;

      // Prepare payload - matching Commercial Supply structure
      const payload = {
        vendorMosymphonyID: currentFormData?.productDetails?.vendorMosymphonyID,
        collabrixProductID: collabrixProductID,
        productFGStatuses: {
          fgStatusesJSON: formattedData,
        },
      };

      console.log("Clinical StatusSelection: payload", {
        vendorMosymphonyID: payload.vendorMosymphonyID,
        collabrixProductID: payload.collabrixProductID,
      });

      let response;

      // Check if this is initial save or update - matching Commercial Supply logic
      // UPDATE path: isInitialSaveSuccess is true OR (editPendingStatus is true AND emptyFgStatuses is false)
      if (isInitialSaveSuccess || (editPendingStatus && !emptyFgStatuses)) {
        console.log("Clinical StatusSelection: entering UPDATE path", {
          isInitialSaveSuccess,
          editPendingStatus,
          emptyFgStatuses,
        });
        // Update existing product (after initial save)
        const updatePayload = {
          ...payload,
          updateFGStatusesFlag: true,
          productDetails: currentFormData?.productDetails,
        };

        // Remove vendorMosymphonyID and collabrixProductID from update payload (not needed in update)
        delete updatePayload.vendorMosymphonyID;
        delete updatePayload.collabrixProductID;

        response = await axios.post(
          `${apiBaseUrl}/updateProductDetails`,
          updatePayload
        );

        // Mark API call as successful
        if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
          console.log("Clinical StatusSelection: UPDATE success", {
            response: response.data,
          });
          // Persist latest data to parent form state
          if (typeof updateStepData === "function") {
            updateStepData("fgStatuses", stepData);
          }
          markApiCallSuccessful(); // This handles both showing success message and navigation
          setIsSaving(false);
          return;
        }
      } else {
        // Initial save API endpoint - for new products (first time)
        // Match Commercial Supply: always call API, backend validates required fields
        console.log("Clinical StatusSelection: entering ADD path", {
          isInitialSaveSuccess,
          editPendingStatus,
          emptyFgStatuses,
        });
        response = await axios.post(
          `${apiBaseUrl}/addProductFgStatuses`,
          payload
        );

        // If initial save is successful
        if (response.data && response.data.status) {
          console.log("Clinical StatusSelection: ADD success", {
            response: response.data,
          });
          setIsInitialSaveSuccess(true);
          // Store in sessionStorage
          sessionStorage.setItem(`pdAddedStatusSelection`, "true");
          sessionStorage.removeItem('pdEmptyFgStatuses');
          setEmptyFgStatuses(false);
          
          // Update formData with product ID if returned
          if (response.data.collabrixProductID && typeof updateStepData === "function") {
            updateStepData("productDetails", {
              ...currentFormData?.productDetails,
              collabrixProductID: response.data.collabrixProductID,
            });
          }

          // Persist latest data to parent form state
          if (typeof updateStepData === "function") {
            updateStepData("fgStatuses", stepData);
          }

          // Mark API call as successful and handle navigation
          if (typeof markApiCallSuccessful === 'function') {
            markApiCallSuccessful();
            setIsSaving(false);
            console.groupEnd();
            return;
          }
        }
      }

      // If we reach here and no markApiCallSuccessful, just proceed to next step
      if (typeof updateStepData === "function") {
        updateStepData("fgStatuses", stepData);
      }
      if (typeof handleNext === "function") {
        handleNext();
      }
    } catch (error) {
      console.error("Clinical StatusSelection: Error saving status selection", error);
      setError("An error occurred while saving. Please try again later.");
      // Still navigate forward even if something goes wrong (for development)
      if (typeof updateStepData === "function") {
        updateStepData("fgStatuses", stepData);
      }
      if (typeof handleNext === "function") {
        handleNext();
      }
    } finally {
      setIsSaving(false);
      console.groupEnd();
    }
  }, [stepData, updateStepData, handleNext, apiBaseUrl, formData, markApiCallSuccessful, isInitialSaveSuccess]);

  return (
    <div>
      {/* Full Page Loader - matching Commercial Supply */}
      {/* Don't show loader when entering edit mode - only show during initial load or saving */}
      {(isLoading || isSaving) && !isStatusEditMode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      <Typography variant="h6" className={parentClasses?.formTitle}>
        Status Selection
      </Typography>
      {error ? (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <Typography variant="body2" style={{ color: "red" }}>
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {readOnly &&
           (!Array.isArray(stepData.poStatuses) || stepData.poStatuses.length === 0) &&
           (!Array.isArray(stepData.batchStatuses) || stepData.batchStatuses.length === 0) ? (
              <Typography align="center" style={{ margin: "32px 0" }}>
                No statuses are selected
              </Typography>
            ) : (
                <Grid container spacing={3} className="stepper-table" style={{ marginTop: "20px" }}>
                  {/* PO Status Selection */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" style={{ marginBottom: "20px" }}>
                      PO Status {!readOnly && <span style={{ color: "red" }}>*</span>}
                    </Typography>
                    <Paper elevation={0}>
                      {renderStatusTable("PO Status", stepData.poStatuses, "postatus")}
                    </Paper>
                  </Grid>

                  {/* Batch Status Selection */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" style={{ marginBottom: "20px" }}>
                      Batch Status
                    </Typography>
                    <Paper elevation={0}>
                      {renderStatusTable("Batch Status", stepData.batchStatuses, "batchstatus")}
                    </Paper>
                  </Grid>
                </Grid>
              )}
        </>
      )}
      {/* Navigation buttons – same pattern as other steppers */}
      {!hideStepActions &&
        !readOnly &&
        typeof handleNext === "function" &&
        typeof handleBack === "function" &&
        typeof activeStep === "number" &&
        typeof stepsLength === "number" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: activeStep === 0 ? "center" : "space-between",
            }}
            className="stepper-navigation-btns"
            mt={4}
          >
            {activeStep !== 0 && (
              <>
                <Button
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={parentClasses?.button}
                >
                  Back
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAndNext}
              disabled={isSaving}
              className={parentClasses?.button}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={16} style={{ marginRight: 8, color: "white" }} />
                  Saving...
                </>
              ) : (
                "Save & Next"
              )}
            </Button>
          </Box>
        )}
    </div>
  );
};

export default StatusSelection;
